import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Button, message, Spin } from 'antd';
import {
  SaveOutlined,
  DollarOutlined,
  PercentageOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CardContainer } from '../../components/contract/CardContainer';
import { SectionHeader } from '../../components/contract/SectionHeader';
import { systemSettingsApi, SystemSettings } from '../../services/api';

const FinancialDefaultsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => systemSettingsApi.get(),
  });

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(settings);
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: (values: SystemSettings) => systemSettingsApi.update(values),
    onSuccess: () => {
      message.success('Financial defaults saved successfully');
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
    onError: () => {
      message.error('Failed to save defaults');
    },
  });

  const handleSubmit = (values: SystemSettings) => {
    saveMutation.mutate(values);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading settings..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <SectionHeader title="Error" subtitle="Unable to load financial defaults" />
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', paddingBottom: '48px' }}>
      <div style={{ marginBottom: '32px' }}>
        <SectionHeader
          title="Financial Defaults"
          subtitle="System-wide defaults for commission, margin, and payment terms. These auto-populate new contracts unless overridden at the supplier level."
          icon={<DollarOutlined style={{ fontSize: '24px' }} />}
          actions={
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={saveMutation.isPending}
              onClick={form.submit}
            >
              Save Changes
            </Button>
          }
          showBottomBorder
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <CardContainer variant="secondary">
            <SectionHeader
              title="Commission"
              subtitle="Default commission rate and share split for new contracts"
              icon={<PercentageOutlined />}
              style={{ marginBottom: '24px' }}
            />

            <Form.Item
              name="defaultCommissionRate"
              label="Commission Rate (%)"
              rules={[
                { required: true, message: 'Commission rate is required' },
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
              ]}
            >
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={0.125}
                precision={3}
                placeholder="e.g. 3.000"
                addonAfter="%"
              />
            </Form.Item>

            <Form.Item
              name="defaultCommissionShare"
              label="Commission Share (%)"
              rules={[
                { required: true, message: 'Commission share is required' },
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
              ]}
              extra="Percentage of commission retained by the company"
            >
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={5}
                precision={2}
                placeholder="e.g. 50.00"
                addonAfter="%"
              />
            </Form.Item>
          </CardContainer>

          <CardContainer variant="secondary">
            <SectionHeader
              title="Margin"
              subtitle="Default margin share for new contracts"
              icon={<DollarOutlined />}
              style={{ marginBottom: '24px' }}
            />

            <Form.Item
              name="defaultMarginShare"
              label="Margin Share (%)"
              rules={[
                { required: true, message: 'Margin share is required' },
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
              ]}
              extra="Percentage of margin retained by the company"
            >
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={5}
                precision={2}
                placeholder="e.g. 50.00"
                addonAfter="%"
              />
            </Form.Item>
          </CardContainer>

          <CardContainer variant="secondary">
            <SectionHeader
              title="Payment Schedule"
              subtitle="Default payment terms for new contracts"
              icon={<CalendarOutlined />}
              style={{ marginBottom: '24px' }}
            />

            <Form.Item
              name="defaultPaymentSchedule"
              label="Payment Schedule"
              rules={[
                { required: true, message: 'Payment schedule is required' },
              ]}
            >
              <Input
                size="large"
                placeholder="e.g. Net 30"
              />
            </Form.Item>
          </CardContainer>
        </div>
      </Form>
    </div>
  );
};

export default FinancialDefaultsPage;
